import React, { useState, useEffect } from 'react';
import { X, Receipt, Calendar } from 'lucide-react';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import { format } from 'date-fns';
import { auth } from '@/services/firebase';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';

interface Transaction {
  id: string;
  orderId: string;
  amountPaidCents: number;
  creditsAdded: number;
  createdAt: string;
  status: string;
}

interface TransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function TransactionItem({ tx }: { tx: Transaction }) {
  return (
    <div className="py-4 first:pt-0 last:pb-0 group">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
        <div>
          <div className="font-semibold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
            {tx.creditsAdded} Credits Added
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mt-1">
            <Calendar className="w-3.5 h-3.5" />
            {format(new Date(tx.createdAt), 'MMM d, yyyy h:mm a')}
          </div>
        </div>
        <div className="sm:text-right">
          <div className="font-medium text-slate-900 dark:text-white">
            ${(tx.amountPaidCents / 100).toFixed(2)}
          </div>
          <div className="text-xs font-medium text-emerald-600 dark:text-emerald-400 mt-1 inline-flex sm:flex uppercase tracking-wider bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 rounded">
            {tx.status}
          </div>
        </div>
      </div>
      <div className="text-xs text-slate-400 dark:text-slate-500 font-mono mt-3 opacity-60">
        Order: {tx.orderId}
      </div>
    </div>
  );
}

function TransactionSkeleton() {
  return (
    <div className="py-4 first:pt-0 last:pb-0">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
        <div className="space-y-2.5 w-full sm:w-auto">
          <div className="h-5 w-32 bg-slate-200 dark:bg-slate-700/50 rounded animate-pulse"></div>
          <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700/50 rounded animate-pulse"></div>
        </div>
        <div className="sm:text-right space-y-2.5 w-full sm:w-auto flex flex-col sm:items-end">
          <div className="h-5 w-16 bg-slate-200 dark:bg-slate-700/50 rounded animate-pulse"></div>
          <div className="h-5 w-24 bg-slate-200 dark:bg-slate-700/50 rounded animate-pulse mt-1"></div>
        </div>
      </div>
      <div className="mt-4">
        <div className="h-3 w-48 bg-slate-200 dark:bg-slate-700/50 rounded animate-pulse"></div>
      </div>
    </div>
  );
}

export function TransactionsModal({ isOpen, onClose }: TransactionsModalProps) {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchTransactions() {
      if (!isOpen) return;
      try {
        setLoading(true);
        const currentUser = auth.currentUser;
        
        let token;
        if (currentUser) {
          token = await currentUser.getIdToken();
        }

        const response = await fetch('/api/user/transactions', {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          cache: 'no-store'
        });

        if (!response.ok) {
          throw new Error('Failed to fetch transactions');
        }

        const data = await response.json();
        setTransactions(data.transactions || []);
      } catch (error: any) {
        toast.error(error.message || 'Could not load history');
      } finally {
        setLoading(false);
      }
    }

    fetchTransactions();
  }, [isOpen]);

  return (
    <ResponsiveOverlay isOpen={isOpen} onClose={onClose} className="p-6 sm:max-w-md w-full max-h-[90vh]">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-emerald-600 dark:text-emerald-400 border border-slate-200 dark:border-slate-700">
            <Receipt className="w-5 h-5" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Transactions</h2>
        </div>
        <Button 
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close transactions"
          className="rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      <div className="overflow-y-auto pr-2 pb-2 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700 max-h-[60vh] space-y-0 divide-y divide-slate-100 dark:divide-slate-800/60">
        {loading ? (
          <>
            <TransactionSkeleton />
            <TransactionSkeleton />
          </>
        ) : transactions.length > 0 ? (
          transactions.map((tx) => (
            <TransactionItem key={tx.id} tx={tx} />
          ))
        ) : (
          <div className="text-center py-12 px-4">
            <Receipt className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <p className="text-slate-900 dark:text-white font-medium">No transactions found</p>
            <p className="text-sm text-slate-500 mt-1">When you purchase credits, your orders will appear here.</p>
          </div>
        )}
      </div>
    </ResponsiveOverlay>
  );
}
